// useFeatureFlag — single source of truth for reading GLOBAL feature flags
// in mobile components.
//
//   const inboxOn = useFeatureFlag('core_inbox_enabled');
//   if (!inboxOn) return <FeatureDisabledScreen flagKey="core_inbox_enabled" />;
//
// The flag key universe is defined by the backend registry at
// backend/api/client/utilities/feature-flags.js. Mobile doesn't keep its
// own list — the redux slice receives whatever the server returns, and
// `useFeatureFlag` resolves unknown / not-yet-fetched keys via the
// `defaultValue` argument (defaults to TRUE so screens stay visible while
// the boot fetch is in flight, matching the migration's NOT NULL DEFAULT
// TRUE for shipped features).
//
// Composition with account_features: feature is available iff the global
// flag AND the per-account entitlement are both on. Most call-sites just
// need the global gate; opt-in walls (MedicationsOptInGate-style) handle
// the per-account half separately.

import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import FeatureDisabledScreen from '../components/FeatureDisabledScreen';

export function useFeatureFlag(key: string, defaultValue: boolean = true): boolean {
  return useSelector((state: RootState) => {
    const slice = state.featureFlags;
    if (!slice?.fetched) return defaultValue;
    const value = slice.flags?.[key];
    return typeof value === 'boolean' ? value : defaultValue;
  });
}

// HOC that wraps a screen so it renders <FeatureDisabledScreen /> when the
// global flag is off. Use at the screen's default-export site:
//
//   function MyScreenInner(props) { ... real screen ... }
//   export default withFeatureGate(MyScreenInner, 'core_X_enabled', 'X');
//
// The wrapper has exactly one hook (`useFeatureFlag`); the inner component
// only mounts when the flag is on. That avoids the conditional-hook trap
// of inlining `if (!enabled) return ...` inside an existing screen body.
export function withFeatureGate<P extends object>(
  Component: React.ComponentType<P>,
  flagKey: string,
  featureName: string,
): React.ComponentType<P> {
  const Gated = (props: P) => {
    const enabled = useFeatureFlag(flagKey);
    if (!enabled) {
      return React.createElement(FeatureDisabledScreen, { featureName, flagKey });
    }
    return React.createElement(Component, props);
  };
  Gated.displayName = `WithFeatureGate(${Component.displayName || Component.name || 'Component'})`;
  return Gated;
}
